import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import api, { notifyApplyQueued } from "../lib/api";
import { toast } from "sonner";
import {
  X, Loader2, Sparkles, Rocket, CheckCircle2, AlertTriangle, Check,
  Save, Download, RotateCcw, ArrowRight, TrendingUp,
} from "lucide-react";
import { Button, inputClass } from "./kit";
import AddYourNumbers, { detectPlaceholders, applyPlaceholders } from "./AddYourNumbers";
import TailoringSkeleton from "./TailoringSkeleton";

// Placeholder authorization wording — keep in sync with backend APPLY_CONSENT_TEXT;
// have it reviewed by legal counsel before launch.
const CONSENT_TEXT =
  "I authorize VetraHub to prepare job applications on my behalf using my resume and profile and — where auto-submission is available — to submit them to the employer's application system. I confirm the information is accurate and that I review each application before it is sent.";

// A labelled read-only value in the materials preview.
function DetailRow({ label, value }) {
  if (!value) return null;
  return (
    <div className="py-2 border-b border-border last:border-0">
      <div className="font-mono text-[9px] uppercase tracking-[0.15em] text-muted-foreground">{label}</div>
      <div className="text-sm text-[#0F172A] truncate">{value}</div>
    </div>
  );
}

// Block field (cover letter / answers) — read-only preview text.
function FieldBlock({ label, value, required }) {
  return (
    <div className="rounded-xl border border-border p-3">
      <div className="text-xs font-semibold text-[#0F172A] mb-1.5">{label}{required && <span className="text-[#E11D48] ml-1">*</span>}</div>
      <p className="text-xs text-muted-foreground leading-relaxed whitespace-pre-wrap line-clamp-4">
        {value || <span className="italic">No answer</span>}
      </p>
    </div>
  );
}

// The prefill "apply-assist" panel shown after approval: everything the
// candidate needs to complete the real form, ready to copy — no auto-submit.
// Contact / system fields shown in "Your details" — never repeated in the questions list.
const SYSTEM_FIELDS = new Set(["first_name", "last_name", "preferred_name", "name", "email", "phone",
  "resume", "resume_text", "cover_letter", "cover_letter_text", "linkedin", "github", "portfolio", "urls"]);
const isSystemField = (nm) => { const n = String(nm || "").toLowerCase(); return SYSTEM_FIELDS.has(n) || n.startsWith("urls["); };

function PrefillPanel({ draft, coverLetter, answers, onFinishAnswers, finishing }) {
  const [downloading, setDownloading] = useState(false);
  const a = draft.applicant || {};
  const job = draft.job || {};

  const downloadResume = async () => {
    if (!draft.resume_structured) { toast.error("No tailored resume on this draft"); return; }
    setDownloading(true);
    try {
      const res = await api.post("/resume/export/docx",
        { resume_structured: draft.resume_structured, name: a.full_name || "Resume" },
        { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const el = document.createElement("a");
      el.href = url;
      el.download = `${(a.full_name || "resume").replace(/\s+/g, "_")}_${(job.company || "job").replace(/\s+/g, "_")}.docx`;
      el.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not download the resume"); }
    finally { setDownloading(false); }
  };

  const _extLane = ["ashby", "icims", "greenhouse", "lever", "smartrecruiters"].includes(draft.job?.ats || draft.ats);
  const STATUS_BANNER = {
    approved:   { cls: "bg-[#10B981]/8 border-[#10B981]/25 text-[#047857]", Icon: CheckCircle2, text: "Approved & ready. Submit it with VetraHub below." },
    queued:     { cls: "bg-[#0025D8]/8 border-[#0025D8]/25 text-[#0025D8]", Icon: Loader2, text: _extLane ? "Applying in your browser — a tab will open; keep it open a few seconds." : "Queued for submission — we'll submit it shortly." },
    ext_queued: { cls: "bg-[#0025D8]/8 border-[#0025D8]/25 text-[#0025D8]", Icon: Loader2, text: "Applying in your browser — a tab will open; keep it open a few seconds." },
    submitting: { cls: "bg-[#0025D8]/8 border-[#0025D8]/25 text-[#0025D8]", Icon: Loader2, text: _extLane ? "Applying in your browser — keep the tab that opened open." : "Submitting…" },
    submitted:  { cls: "bg-[#10B981]/8 border-[#10B981]/25 text-[#047857]", Icon: CheckCircle2, text: draft.receipt?.confirmation || "Submitted." },
    failed:     { cls: "bg-[#E11D48]/8 border-[#E11D48]/25 text-[#E11D48]", Icon: AlertTriangle, text: draft.receipt?.error || "Submission failed — try submitting again." },
    needs_user: { cls: "bg-[#F59E0B]/8 border-[#F59E0B]/25 text-[#B45309]", Icon: AlertTriangle, text: draft.receipt?.error || "Needs your attention before we can submit." },
  };
  const banner = STATUS_BANNER[draft.status] || STATUS_BANNER.approved;

  return (
    <div className="space-y-5">
      <div className={`rounded-xl border px-4 py-3 flex items-start gap-3 ${banner.cls}`}>
        <banner.Icon className="w-4 h-4 flex-shrink-0 mt-0.5" />
        <p className="text-xs leading-relaxed">{banner.text}</p>
      </div>

      {/* Your details */}
      <div>
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-2">Your details</div>
        <div className="rounded-xl border border-border px-3">
          <DetailRow label="Full name" value={a.full_name} />
          <DetailRow label="Email" value={a.email} />
          <DetailRow label="Phone" value={a.phone} />
          <DetailRow label="LinkedIn" value={a.linkedin_url} />
          <DetailRow label="GitHub" value={a.github_url} />
          <DetailRow label="Portfolio" value={a.portfolio_url} />
        </div>
      </div>

      {/* Resume */}
      <div>
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-2">Tailored resume</div>
        <Button variant="secondary" size="sm" onClick={downloadResume} loading={downloading}>
          <Download className="w-3.5 h-3.5" /> Download resume (DOCX)
        </Button>
      </div>

      {/* Cover letter */}
      <div>
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-2">Cover letter</div>
        <FieldBlock label="Cover letter" value={coverLetter} />
      </div>

      {/* Screening questions — contact fields live in "Your details" above; optional blanks are hidden,
          so this shows only real questions: the ones we answered, plus any required one that needs you. */}
      {(() => {
        const has = (x) => !!(x.answer && String(x.answer).trim());
        const rows = (answers || []).filter((x) => !isSystemField(x.name) && (has(x) || x.required));
        const answered = rows.filter(has);
        const missing = rows.filter((x) => !has(x));
        if (rows.length === 0) return null;
        return (
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-2">Screening questions</div>
            <div className="space-y-2">
              {answered.map((x, i) => <FieldBlock key={x.name || `a${i}`} label={x.label} value={x.answer} />)}
              {missing.map((x, i) => (
                <div key={x.name || `m${i}`} className="rounded-xl border border-[#F59E0B]/40 bg-[#F59E0B]/[0.06] p-3">
                  <div className="text-xs font-semibold text-[#0F172A] mb-1">{x.label}<span className="text-[#E11D48] ml-1">*</span></div>
                  <p className="text-xs text-[#B45309] italic">Needs your answer</p>
                </div>
              ))}
            </div>
            {missing.length > 0 && onFinishAnswers && (
              <Button variant="secondary" size="sm" className="mt-3" onClick={onFinishAnswers} loading={finishing}>
                Finish {missing.length} question{missing.length > 1 ? "s" : ""} →
              </Button>
            )}
          </div>
        );
      })()}
    </div>
  );
}

// One-click apply — Apply modal (Phase 2.5). Paste a job link → AI tailors the
// application (resume diff + cover letter + answers) → review/edit → approve.
// Submission itself is Phase 3; approval only marks the draft ready.

const SkillChip = ({ children, tone }) => (
  <span className={`inline-flex items-center gap-1 font-mono text-[10px] px-2 py-1 rounded border ${
    tone === "good" ? "border-[#10B981]/30 bg-[#10B981]/10 text-[#047857]"
                    : "border-[#F59E0B]/30 bg-[#F59E0B]/10 text-[#B45309]"}`}>
    {children}
  </span>
);


// What this job asked for that the résumé could not evidence — and the one place to act on it.
//
// The rewrite will not claim any of these on its own: a skill the résumé cannot support is recorded
// here instead of being asserted. But the gap is usually not that the candidate LACKS the thing — it
// is that their résumé never mentioned it. So they are offered for the candidate to CLAIM.
//
// The distinction is the whole point. Ticking is the candidate stating something about themselves,
// which they can stand behind in the interview; the alternative is the system inventing it on their
// behalf and auto-submitting it under their name. Nothing is added unless it is ticked here, so with
// review turned off nothing is added at all.
function SkillGaps({ gaps, onRetailor }) {
  const [picked, setPicked] = useState([]);
  const [saving, setSaving] = useState(false);
  // NO PARSING HERE. Each gap arrives as {skill, why} — the backend normalises both the current
  // shape and the prose older drafts stored (see _normalize_skill_gaps). This component used to
  // split the sentence itself on a colon, which silently dropped every gap the model wrote with an
  // em dash instead: on one real draft that was all nine of them, and the panel rendered empty.
  //
  // EVERY GAP IS SHOWN. It was capped at eight, so a job with more left the rest invisible until
  // the candidate ticked some and re-tailored — at which point the remainder appeared as a second,
  // apparently different set of skills. One list, one decision, one rewrite.
  const items = useMemo(
    () => (gaps || []).filter((g) => g && typeof g === "object" && g.skill),
    [gaps]);
  const toggle = (s) => setPicked((p) => p.includes(s) ? p.filter((x) => x !== s) : [...p, s]);

  const addAndRewrite = async () => {
    if (!picked.length) return;
    setSaving(true);
    try {
      const { data } = await api.get("/apply/profile");
      const prof = data.profile || data;
      const next = [...new Set([...(prof.confirmed_skills || []), ...picked])];
      await api.put("/apply/profile", { ...prof, confirmed_skills: next });
      toast.success(`Added ${picked.length} skill${picked.length === 1 ? "" : "s"} — rewriting your résumé`);
      // Pass what was ticked, so the rewrite removes exactly these from the frozen list rather
      // than re-deriving a fresh one.
      await onRetailor?.(picked);
    } catch {
      toast.error("Couldn't save those — try again.");
    } finally { setSaving(false); }
  };

  if (!items.length) return null;
  return (
    <div className="mt-3 rounded-xl border border-[#F59E0B]/35 bg-[#F59E0B]/[0.05] p-3">
      <div className="text-[12px] font-bold text-[#0F172A]">This job also asked for</div>
      <p className="text-[11px] text-[#0F172A]/70 mt-0.5 leading-relaxed">
        Your résumé doesn't show these, so they were left off. Tick anything you have — it goes on
        this résumé and every future one. <span className="font-semibold">Only tick what you could
        discuss in an interview.</span>
      </p>
      <div className="flex flex-wrap gap-1.5 mt-2.5">
        {items.map(({ skill, why }) => {
          const on = picked.includes(skill);
          return (
            <button key={skill} type="button" onClick={() => toggle(skill)} disabled={saving}
              title={why || undefined}
              className={`inline-flex items-center gap-1.5 text-[12px] px-2 py-1 rounded-lg border transition-colors disabled:opacity-50 ${
                on ? "border-[#059669]/40 bg-[#059669]/[0.10] text-[#047857] font-semibold"
                   : "border-dashed border-[#0F172A]/20 bg-white hover:border-[#059669]/50 text-[#0F172A]"}`}>
              {on ? <Check className="w-3 h-3" /> : <span className="text-muted-foreground">+</span>}
              {skill}
            </button>
          );
        })}
      </div>
      {picked.length > 0 && (
        <Button size="sm" className="mt-3" loading={saving} onClick={addAndRewrite}>
          <RotateCcw className="w-3.5 h-3.5" />
          Add {picked.length} and rewrite my résumé
        </Button>
      )}
    </div>
  );
}

function ReviewBody({ draft, coverLetter, setCoverLetter, answers, setAnswer, placeholders, metricValues, setMetric, onRetailor }) {
  const job = draft.job || {};
  // Whether the tickable "This job also asked for" panel will render. Same condition it uses, so
  // the two cannot disagree about which list owns the missing skills.
  const hasGapPanel = (draft.skill_gaps || []).some((g) => g && typeof g === "object" && g.skill);
  const before = draft.match_score;              // original résumé vs JD
  const after = draft.post_ats_score;            // optimized/tailored résumé estimate
  // Only show the Before→After uplift when it's real AND the baseline is trustworthy (a 0 usually means
  // the original score didn't compute — showing "0% → 92%" would be misleading).
  const improved = before > 0 && after != null && after > before;
  const single = improved ? null : (after != null ? after : before);    // fallback single number
  return (
    <div className="space-y-6">
      {/* Match — Original → Optimized (shows the tailoring uplift when there is one) */}
      <div>
        <div className="flex items-center justify-between gap-3 mb-3">
          <div className="min-w-0">
            <div className="font-display font-bold text-lg text-[#0F172A]">{job.title || "Role"}</div>
            <div className="text-sm text-muted-foreground">{job.company} · <span className="uppercase font-mono text-[10px]">{job.ats}</span></div>
          </div>
          {improved ? (
            <div className="flex items-center gap-2.5 flex-shrink-0">
              <div className="text-center">
                <div className="font-display font-bold text-xl text-slate-400 tabular-nums leading-none">{before}<span className="text-xs">%</span></div>
                <div className="font-mono text-[8px] text-muted-foreground tracking-[0.12em] mt-1">ORIGINAL</div>
              </div>
              <ArrowRight className="w-4 h-4 text-slate-300 flex-shrink-0" />
              <div className="text-center">
                <div className="font-display font-black text-3xl text-[#0025D8] tabular-nums leading-none">{after}<span className="text-sm">%</span></div>
                <div className="font-mono text-[8px] text-[#0025D8] tracking-[0.12em] mt-1">OPTIMIZED</div>
              </div>
              <span className="inline-flex items-center gap-0.5 text-[11px] font-extrabold text-[#047857] bg-[#10B981]/12 px-1.5 py-1 rounded-lg self-stretch">
                <TrendingUp className="w-3 h-3" /> +{after - before}
              </span>
            </div>
          ) : single != null ? (
            <div className="text-right flex-shrink-0">
              <div className="font-display font-black text-3xl text-[#0025D8] tabular-nums leading-none">{single}<span className="text-sm">%</span></div>
              <div className="font-mono text-[9px] text-muted-foreground tracking-[0.15em] mt-1">MATCH</div>
            </div>
          ) : null}
        </div>
        {/* MISSING SKILLS APPEAR ONCE. The tickable panel below says the same thing and lets the
            candidate act on it, so when it renders these amber chips are the same list again in
            different words — measured on a real draft, 3 literal duplicates and 3 more that were
            the same skill reworded ("Objective-C experience" vs "Objective-C familiarity"). They
            stay as a fallback because the tailor sometimes returns no gaps at all while the scorer
            still found missing skills, and that draft would otherwise say nothing about them. */}
        {(draft.matched_skills?.length || (!hasGapPanel && draft.missing_skills?.length)) ? (
          <div className="flex flex-wrap gap-1.5">
            {(draft.matched_skills || []).slice(0, 8).map((s) => <SkillChip key={s} tone="good"><Check className="w-2.5 h-2.5" /> {s}</SkillChip>)}
            {!hasGapPanel && (draft.missing_skills || []).slice(0, 6).map((s) => <SkillChip key={s} tone="warn"><AlertTriangle className="w-2.5 h-2.5" /> {s}</SkillChip>)}
          </div>
        ) : null}
      </div>

      {/* Add your numbers — fill the metric placeholders the optimizer flagged as "Verify" */}
      <AddYourNumbers placeholders={placeholders} values={metricValues} onChange={setMetric} />

      {/* Resume changes (diff) */}
      {draft.changes_made?.length > 0 && (
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-3">Resume tailoring</div>
          <ul className="space-y-2">
            {draft.changes_made.map((c, i) => {
              // Entries are {text, confidence} objects. A prompt that returns plain strings instead
              // used to render a badge beside nothing at all — a page of empty rows with no clue
              // what went wrong. Accept either shape so a schema slip costs formatting, not content.
              const text = typeof c === "string" ? c : (c?.text || "");
              const consider = c?.confidence === "CONSIDER";
              if (!text) return null;
              return (
                <li key={i} className="flex items-start gap-2.5 rounded-xl border border-border p-3">
                  <span className={`font-mono text-[8px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded mt-0.5 flex-shrink-0 ${
                    consider ? "bg-[#F59E0B]/10 text-[#B45309]" : "bg-[#10B981]/10 text-[#047857]"}`}>
                    {consider ? "Verify" : "Applied"}
                  </span>
                  <span className="text-sm text-[#0F172A] leading-snug">{text}</span>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* Outside the tailoring block on purpose: the gaps are worth acting on even when there was
          nothing to rewrite (Original mode produces no change log at all). */}
      {/* Same flag the chips above are suppressed by, so the two can never disagree about which
          list is showing the missing skills. */}
      {hasGapPanel && <SkillGaps gaps={draft.skill_gaps} onRetailor={onRetailor} />}

      {/* Cover letter */}
      <div>
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-2">Cover letter</div>
        <textarea value={coverLetter} onChange={(e) => setCoverLetter(e.target.value)} rows={8}
          placeholder="Your cover letter…" className={`${inputClass} resize-y leading-relaxed`} />
      </div>

      {/* Open-ended answers */}
      {answers.length > 0 && (
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-2">Application questions</div>
          <div className="space-y-3">
            {answers.map((a, i) => (
              <div key={a.name || i}>
                <label className="block text-xs font-semibold text-[#0F172A] mb-1.5">
                  {a.label}{a.required && <span className="text-[#E11D48] ml-1">*</span>}
                </label>
                <textarea value={a.answer || ""} onChange={(e) => setAnswer(i, e.target.value)} rows={a.type === "long_text" ? 4 : 2}
                  placeholder="Your answer…" className={`${inputClass} resize-y`} />
              </div>
            ))}
          </div>
        </div>
      )}

      <p className="flex items-start gap-2 text-[11px] text-muted-foreground rounded-xl bg-secondary/50 border border-border px-3 py-2.5">
        <Sparkles className="w-3.5 h-3.5 flex-shrink-0 mt-0.5 text-[#0025D8]" />
        AI drafts from your resume and saved answers only — review everything before approving. Nothing is submitted yet.
      </p>
    </div>
  );
}

// `draftId` (optional) opens an existing draft straight into review.
// `initialUrl` (optional) pre-fills the job link for a new application (deep-link).
// `onChanged` fires after a save/approve/create so a parent list can refresh.
export default function ApplyModal({ open, onClose, draftId = null, initialUrl = "", autoStart = false, onChanged }) {
  const [step, setStep] = useState("start");      // start | loading | review | approved
  const [url, setUrl] = useState("");
  const [resumes, setResumes] = useState([]);
  const [resumeId, setResumeId] = useState("");
  const [draft, setDraft] = useState(null);
  const [coverLetter, setCoverLetter] = useState("");
  const [answers, setAnswers] = useState([]);
  const [busy, setBusy] = useState(false);
  const [consented, setConsented] = useState(false);       // already authorized on file
  const [consentChecked, setConsentChecked] = useState(false);
  const [metricValues, setMetricValues] = useState({});    // placeholder id -> candidate number
  const [finishing, setFinishing] = useState(false);
  const navigate = useNavigate();

  // "Finish answers" from the ready preview: resolve the REAL missing required questions (accurate — the
  // draft's answer snapshot can be stale) and open the Save & retry page; if nothing's actually missing,
  // just reassure and stay.
  const finishAnswers = async () => {
    if (!draft?.draft_id) return;
    setFinishing(true);
    try {
      const { data } = await api.post(`/apply/draft/${draft.draft_id}/finish-answers`);
      if (data.complete) { toast.success("Everything's answered — you're ready to submit."); }
      else { onClose(); navigate(`/apply/status/${draft.draft_id}`); }
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not open the questions."); }
    finally { setFinishing(false); }
  };

  // Metric placeholders the candidate can fill in during review ([X%], [X days]…).
  const placeholders = useMemo(
    () => (draft?.resume_structured ? detectPlaceholders(draft.resume_structured) : []),
    [draft]
  );
  const setMetric = (id, val) => setMetricValues((m) => ({ ...m, [id]: val }));
  // Resume with the candidate's numbers filled in; blanks are cleaned away.
  const finalResume = () =>
    placeholders.length ? applyPlaceholders(draft.resume_structured, metricValues) : draft?.resume_structured;

  const loadDraftIntoReview = (d) => {
    setDraft(d);
    setCoverLetter(d.cover_letter || "");
    setAnswers(d.answers || []);
    setMetricValues({});
    // Only 'draft' is editable; approved/queued/submitting/submitted/failed/needs_user
    // all open the materials + status view.
    setStep(d.status === "draft" ? "review" : "approved");
  };

  // On open: reopen an existing draft, or start fresh (loading saved resumes).
  useEffect(() => {
    if (!open) return;
    setUrl(initialUrl || ""); setDraft(null); setCoverLetter(""); setAnswers([]); setBusy(false); setConsentChecked(false);
    api.get("/apply/profile")
      .then(({ data }) => setConsented(!!data.profile?.apply_authorization?.agreed))
      .catch(() => {});
    if (draftId) {
      setStep("loading");
      api.get(`/apply/draft/${draftId}`)
        .then(({ data }) => loadDraftIntoReview(data.draft))
        .catch(() => { toast.error("Could not load that application"); onClose(); });
    } else {
      setStep("start");
      api.get("/resume/saved").then(({ data }) => setResumes(data.resumes || [])).catch(() => setResumes([]));
      // Seamless hand-off from a list "Apply on Autopilot" click: start tailoring immediately.
      if (autoStart && (initialUrl || "").trim()) tailor(initialUrl);
    }
    const onKey = (e) => { if (e.key === "Escape" && step !== "loading") onClose(); };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = prev; };
  }, [open, draftId, initialUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!open) return null;

  // Rebuild THIS draft against the current profile — used after the candidate confirms skills the
  // résumé could not evidence. Deliberately not `tailor(url)`: draft creation is idempotent per
  // (user, url) so a repeat call is refused as an already-started application, by design.
  // `taken` is the exact chip labels just ticked. The draft records them so those skills leave its
  // gap list by identity — the list is frozen at the first tailoring and only ever shrinks.
  const retailor = async (taken = []) => {
    if (!draft?.draft_id) return;
    setStep("loading");
    try {
      const { data } = await api.post(`/apply/draft/${draft.draft_id}/retailor`, { taken });
      setDraft(data.draft);
      setCoverLetter(data.draft.cover_letter || "");
      setAnswers(data.draft.answers || []);
      setMetricValues({});
      onChanged?.(data.draft);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't rewrite that — your draft is unchanged.");
    } finally { setStep("review"); }
  };

  const tailor = async (overrideUrl) => {
    const u = ((typeof overrideUrl === "string" ? overrideUrl : url) || "").trim();
    if (!u) { toast.error("Paste a job link to tailor your application."); return; }
    setStep("loading");
    try {
      const { data } = await api.post("/apply/draft", { url: u, resume_id: resumeId || null });
      setDraft(data.draft);
      setCoverLetter(data.draft.cover_letter || "");
      setAnswers(data.draft.answers || []);
      setMetricValues({});
      setStep("review");
      onChanged?.(data.draft);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error((d && (d.message || d)) || "Could not tailor this application. Try another link.");
      setStep("start");
    }
  };

  const setAnswer = (i, val) => setAnswers((prev) => prev.map((a, idx) => idx === i ? { ...a, answer: val } : a));
  const payload = () => ({ cover_letter: coverLetter, answers, resume_structured: finalResume() });

  const saveDraft = async () => {
    setBusy(true);
    try {
      await api.patch(`/apply/draft/${draft.draft_id}`, payload());
      toast.success("Draft saved");
      onChanged?.(draft);
    } catch { toast.error("Could not save draft"); }
    finally { setBusy(false); }
  };

  const approve = async () => {
    if (!consented && !consentChecked) { toast.error("Please tick the authorization box to continue."); return; }
    setBusy(true);
    try {
      if (!consented && consentChecked) {
        await api.post("/apply/consent", { agreed: true });
        setConsented(true);
      }
      const fr = finalResume();
      await api.post(`/apply/draft/${draft.draft_id}/approve`, { cover_letter: coverLetter, answers, resume_structured: fr });
      setDraft((d) => ({ ...d, status: "approved", resume_structured: fr }));   // so the Submit button shows
      setStep("approved");
      onChanged?.({ ...draft, status: "approved" });
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error((d && (d.message || d)) || "Could not approve. Try again.");
    } finally { setBusy(false); }
  };

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/apply/draft/${draft.draft_id}/submit`);
      toast.success("Queued for submission — track it in Applications");
      onChanged?.({ ...draft, status: "queued" });
      onClose();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error((d && (d.message || d)) || "Could not submit. Try again.");
    } finally { setBusy(false); }
  };

  const retry = async () => {
    setBusy(true);
    try {
      await api.post(`/apply/draft/${draft.draft_id}/retry`);
      notifyApplyQueued();
      toast.success("Re-queued — we'll try this application again. Track it in Applications.");
      onChanged?.({ ...draft, status: "queued" });
      onClose();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error((d && (d.message || d)) || "Could not retry. Try again.");
    } finally { setBusy(false); }
  };

  return createPortal(
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={() => step !== "loading" && onClose()} />
      <div className="relative bg-white rounded-2xl border border-border w-full max-w-2xl max-h-[92vh] flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border flex-shrink-0">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-[#EA580C]/10 flex items-center justify-center">
              <Rocket className="w-4 h-4 text-[#EA580C]" />
            </div>
            <div>
              <div className="font-mono text-[9px] tracking-[0.2em] uppercase text-[#EA580C]">One-click apply · Beta</div>
              <div className="font-semibold text-sm text-[#0F172A]">
                {step === "review" ? "Review your application" : step === "approved" ? "Application ready" : "Tailor an application"}
              </div>
            </div>
          </div>
          <button onClick={() => step !== "loading" && onClose()} className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {step === "start" && (
            <div className="space-y-5">
              <div>
                <label className="block text-xs font-semibold text-[#0F172A] mb-1.5">Job link</label>
                <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://boards.greenhouse.io/…"
                  className={inputClass} onKeyDown={(e) => { if (e.key === "Enter") tailor(); }} />
                <p className="text-[11px] text-muted-foreground mt-1.5">Works with Greenhouse, Lever, Ashby, SmartRecruiters, Workday, iCIMS, Workable, BambooHR, Breezy, Rippling, UKG, Dayforce, Zoho, JazzHR and SuccessFactors.</p>
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#0F172A] mb-1.5">Resume to tailor</label>
                <select value={resumeId} onChange={(e) => setResumeId(e.target.value)} className={`${inputClass} cursor-pointer`}>
                  <option value="">{resumes.length ? "Use my default / most recent" : "No saved resumes"}</option>
                  {resumes.map((r) => <option key={r.resume_id} value={r.resume_id}>{r.name || "Untitled resume"}</option>)}
                </select>
                {!resumes.length && (
                  <p className="text-[11px] text-[#B45309] mt-1.5">Save a resume in the Resume Analyzer first so we can tailor it.</p>
                )}
              </div>
            </div>
          )}

          {step === "loading" && <TailoringSkeleton />}

          {step === "review" && draft && (
            <ReviewBody draft={draft} onRetailor={retailor}
              coverLetter={coverLetter} setCoverLetter={setCoverLetter} answers={answers} setAnswer={setAnswer}
              placeholders={placeholders} metricValues={metricValues} setMetric={setMetric} />
          )}

          {step === "approved" && draft && (
            <PrefillPanel draft={draft} coverLetter={coverLetter} answers={answers} onFinishAnswers={finishAnswers} finishing={finishing} />
          )}
        </div>

        {/* Footer — start */}
        {step === "start" && (
          <div className="flex items-center justify-between gap-2 px-6 py-4 border-t border-border flex-shrink-0">
            <Button variant="ghost" size="sm" onClick={onClose}>Cancel</Button>
            <Button onClick={tailor} disabled={!resumes.length}><Sparkles className="w-4 h-4" /> Tailor application</Button>
          </div>
        )}
        {/* Footer — review (with authorization gate) */}
        {step === "review" && (
          <div className="px-6 py-4 border-t border-border flex-shrink-0 space-y-3">
            {!consented && (
              <label className="flex items-start gap-2.5 cursor-pointer">
                <input type="checkbox" checked={consentChecked} onChange={(e) => setConsentChecked(e.target.checked)}
                  className="mt-0.5 w-4 h-4 accent-[#0025D8] flex-shrink-0" />
                <span className="text-[11px] text-muted-foreground leading-relaxed">{CONSENT_TEXT}</span>
              </label>
            )}
            <div className="flex items-center justify-between gap-2">
              <Button variant="secondary" size="sm" onClick={saveDraft} loading={busy}><Save className="w-3.5 h-3.5" /> Save draft</Button>
              <Button onClick={approve} loading={busy} disabled={!consented && !consentChecked}><CheckCircle2 className="w-4 h-4" /> Approve application</Button>
            </div>
          </div>
        )}
        {step === "approved" && (
          <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-border flex-shrink-0">
            <Button variant="ghost" size="sm" onClick={onClose}>Done</Button>
            {draft?.status === "approved" ? (
              <Button onClick={submit} loading={busy} className="min-w-[210px] justify-center">
                <Rocket className="w-4 h-4" /> Submit with VetraHub
              </Button>
            ) : (draft?.status === "failed" || draft?.status === "needs_user") ? (
              <Button onClick={retry} loading={busy} className="min-w-[180px] justify-center">
                <RotateCcw className="w-4 h-4" /> Retry application
              </Button>
            ) : ["queued", "submitting"].includes(draft?.status) ? (
              <span className="inline-flex items-center gap-2 text-sm font-medium text-[#0025D8]">
                <Loader2 className="w-4 h-4 animate-spin" /> Submitting…
              </span>
            ) : draft?.status === "submitted" ? (
              <span className="inline-flex items-center gap-2 text-sm font-medium text-[#047857]">
                <CheckCircle2 className="w-4 h-4" /> Submitted
              </span>
            ) : null}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
